import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { preloadSpaceGuestList } from '@confluence/space-guest-list/entry-points/preloadSpaceGuestList.ts';
import { preloadShortcutsSmartLinks } from '@confluence/space-shortcuts/entry-points/preloadShortcutsSmartLinks.ts';
import { preloadSideNavigation } from '@confluence/side-navigation/entry-points/preloadSideNavigation.ts';
import { createSpaceNavigationQueryPreloader } from '@confluence/side-navigation/entry-points/preloadSpaceNavigationQuery.ts';
import { preloadSpaceViews } from '@confluence/space-views/entry-points/preloadSpaceViews.ts';
import { preloadPageTree } from '@confluence/page-tree/entry-points/preloadPageTree.ts';
import {
	isBlogTreeExpanded,
	preloadBlogTree,
} from '@confluence/blog-tree/entry-points/preloadBlogTree.ts';
import { PAGE_TREE_STATUSES } from '@confluence/page-tree/entry-points/pageTreeStatuses.ts';
import { INITIAL_PAGE_SIZE } from '@confluence/page-tree/entry-points/paginationLimits.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { preloadIsUserViewingSpaceAsAnon } from '@confluence/anonymous-access/entry-points/preloadIsUserViewingSpaceAsAnon.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';

/** Prepare only the route query when the rest of navigation is already mounted. */
export async function preloadNavigationQuery(
	spaceKey: string,
	contentId: string,
	isBlog: boolean,
): Promise<void> {
	if (
		spaceKey &&
		!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_preloaded_query') &&
		(expVal('confluence_exp_space_nav_contract', 'isEnabled', false) ||
			expVal('confluence_exp_space_nav_combined_links', 'isEnabled', false))
	) {
		const preloadQuery = createSpaceNavigationQueryPreloader();
		const includeBlogTree = isBlog || (await isBlogTreeExpanded());
		const includePageTree = Boolean(contentId) && !isBlog;
		const queryResult = await preloadQuery(spaceKey, {
			contentId,
			includeBlogTree,
			includePageTree,
			includeHomepageTreeVariant: !includePageTree,
			isSuperAdmin: false,
			paginationLimit: INITIAL_PAGE_SIZE,
			ptStatuses: PAGE_TREE_STATUSES,
		});
		// Same-space query-only transitions still hydrate Smart Cards from the route result.
		if (
			!queryResult.cancelled &&
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_core_frag_reader') &&
			expVal('confluence_exp_space_nav_combined_links', 'isEnabled', false)
		) {
			const { cloudId } = getPreloaderFnContext();
			await preloadShortcutsSmartLinks(spaceKey, {
				spaceKey,
				cloudId,
				data: { combinedSpaceSidebarLinks: queryResult.data?.combinedSpaceSidebarLinks },
			});
		}
	}
}

export const preloadNavigationTasks = async (
	spaceKey: string,
	contentId: string,
	isLicensed: boolean,
	isBlog: boolean,
	useSideNavigationBoundary = false,
	previousSpaceKey?: string,
): Promise<void | any[] | Error[]> => {
	const preloadNavigation = () => {
		const preloadQuery =
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_preloaded_query') &&
			(expVal('confluence_exp_space_nav_contract', 'isEnabled', false) ||
				expVal('confluence_exp_space_nav_combined_links', 'isEnabled', false))
				? createSpaceNavigationQueryPreloader()
				: undefined;
		const includePageTree = Boolean(contentId) && !isBlog;
		const includeHomepageTreeVariant = !includePageTree;
		const includeBlogTreePromise = isBlog ? Promise.resolve(true) : isBlogTreeExpanded();

		// Route-owned navigation handles shortcuts for the existing contract cohort too:
		// standalone in control, combined response in the new treatment.
		const shouldSkipShortcuts =
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_core_frag_reader') &&
			(expVal('confluence_exp_space_nav_contract', 'isEnabled', false) ||
				expVal('confluence_exp_space_nav_combined_links', 'isEnabled', false));

		return waitForAllReturnWhenError([
			// Guest eligibility comes from the route contract; fetch dialog data when it opens.
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_affordances_frag_reader') &&
			expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
				? Promise.resolve()
				: preloadSpaceGuestList(spaceKey),
			shouldSkipShortcuts ? Promise.resolve() : preloadShortcutsSmartLinks(spaceKey),
			includeBlogTreePromise.then((includeBlogTree) =>
				preloadSideNavigation(
					spaceKey,
					previousSpaceKey,
					{
						contentId,
						includeBlogTree,
						includeHomepageTreeVariant,
						includePageTree,
						isSuperAdmin: false,
						paginationLimit: INITIAL_PAGE_SIZE,
						ptStatuses: PAGE_TREE_STATUSES,
					},
					preloadQuery,
				),
			),
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_space_views_frag_reader') &&
			expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
				? Promise.resolve()
				: preloadSpaceViews(spaceKey, isLicensed),
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_affordances_frag_reader') &&
			expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
				? Promise.resolve()
				: preloadIsUserViewingSpaceAsAnon(spaceKey),
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_trees_frag_reader') &&
			expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
				? Promise.resolve()
				: preloadPageTree({
						spaceKey,
						...(isBlog ? {} : { contentId }),
						statuses: PAGE_TREE_STATUSES,
						hasSpaceCreationEligibility:
							!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_creation_frag_reader') &&
							expVal('confluence_exp_space_nav_contract', 'isEnabled', false),
						isSuperAdmin: false,
					}),
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_trees_frag_reader') &&
			expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
				? Promise.resolve()
				: preloadBlogTree({ spaceKey }),
		]);
	};

	return useSideNavigationBoundary
		? createDataFetcherForSuspense('side-navigation', preloadNavigation)
		: preloadNavigation();
};
